import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Button,
  Dropdown,
  Empty,
  Popconfirm,
  Space,
  Spin,
  Tag,
  Tooltip,
  theme
} from "antd";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  FolderOutlined,
  PaperClipOutlined,
  StarFilled,
  StarOutlined
} from "@ant-design/icons";
import { FaReply, FaReplyAll, FaShare } from "react-icons/fa";
import { Error } from "../../../commons/Messages/Messages";
import { mailApi, getErrorMessage } from "./mailApi";
import MessageBody from "./MessageBody";
import {
  displayName,
  formatAddress,
  formatAddressList,
  formatFullDate,
  formatSize,
  hasRemoteContent
} from "./mailUtils";
import { useMailTranslation } from "./useMailTranslation";
import AssistantPanel from "./assistant/AssistantPanel";

const MessageView = ({
  account,
  folder,
  uid,
  folders,
  isDraftsFolder,
  onCompose,
  onFlagsChanged,
  onRemoved,
  onBack,
  assistant
}) => {
  const { t } = useMailTranslation();
  const { token } = theme.useToken();
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [allowRemote, setAllowRemote] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setAllowRemote(false);
    setMessage(null);
    if (!uid) return undefined;
    setLoading(true);
    mailApi
      .getMessage(account._id, uid, folder)
      .then(res => {
        if (cancelled) return;
        setMessage(res.data);
        onFlagsChanged([uid], { seen: true });
      })
      .catch(error => !cancelled && Error(getErrorMessage(error, t("Failed to load message"))))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [account._id, folder, uid]);

  if (!uid) {
    return (
      <div className="mail-reader-empty">
        <Empty description={t("Select a message to read")} />
      </div>
    );
  }
  if (loading || !message) {
    return (
      <div className="mail-reader-empty">
        <Spin />
      </div>
    );
  }

  const setFlag = async (flag, value, localPatch) => {
    try {
      await mailApi.updateFlags(account._id, {
        folder,
        uids: [uid],
        add: value ? [flag] : [],
        remove: value ? [] : [flag]
      });
      setMessage(prev => ({ ...prev, ...localPatch }));
      onFlagsChanged([uid], localPatch);
    } catch (error) {
      Error(getErrorMessage(error, t("Failed to update message")));
    }
  };

  const handleMove = async destination => {
    try {
      await mailApi.moveMessages(account._id, { folder, uids: [uid], destination });
      onRemoved([uid]);
    } catch (error) {
      Error(getErrorMessage(error, t("Failed to move message")));
    }
  };

  const handleDelete = async () => {
    try {
      await mailApi.deleteMessages(account._id, { folder, uids: [uid] });
      onRemoved([uid]);
    } catch (error) {
      Error(getErrorMessage(error, t("Failed to delete message")));
    }
  };

  const moveTargets = folders
    .filter(item => item.selectable && item.path !== folder)
    .map(item => ({ key: item.path, label: item.path === "INBOX" ? t("Inbox") : item.path }));
  const fileAttachments = message.attachments.filter(attachment => !attachment.inline);
  const sender = message.from[0] || {};
  const showRemoteBanner = !allowRemote && hasRemoteContent(message.html);

  return (
    <div className="mail-reader">
      <div className="mail-reader-toolbar">
        <Space wrap size={4}>
          {onBack && (
            <Button
              type="text"
              className="mail-back"
              icon={<ArrowLeftOutlined />}
              onClick={onBack}
              aria-label={t("Back")}
            />
          )}
          {isDraftsFolder ? (
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => onCompose("draft", message)}
            >
              {t("Edit draft")}
            </Button>
          ) : (
            <>
              <Button
                icon={<FaReply />}
                onClick={() => onCompose("reply", message)}
                title={t("Reply")}
              >
                <span className="mail-btn-label">{t("Reply")}</span>
              </Button>
              <Button
                icon={<FaReplyAll />}
                onClick={() => onCompose("replyAll", message)}
                title={t("Reply all")}
              >
                <span className="mail-btn-label">{t("Reply all")}</span>
              </Button>
              <Button
                icon={<FaShare />}
                onClick={() => onCompose("forward", message)}
                title={t("Forward")}
              >
                <span className="mail-btn-label">{t("Forward")}</span>
              </Button>
            </>
          )}
        </Space>
        <Space size={4}>
          <Tooltip title={message.flagged ? t("Remove star") : t("Star")}>
            <Button
              type="text"
              aria-label={message.flagged ? t("Remove star") : t("Star")}
              icon={
                message.flagged ? (
                  <StarFilled style={{ color: token.colorWarning }} />
                ) : (
                  <StarOutlined />
                )
              }
              onClick={() => setFlag("\\Flagged", !message.flagged, { flagged: !message.flagged })}
            />
          </Tooltip>
          <Tooltip title={t("Mark as unread")}>
            <Button
              type="text"
              aria-label={t("Mark as unread")}
              icon={<EyeInvisibleOutlined />}
              onClick={() => setFlag("\\Seen", false, { seen: false })}
            />
          </Tooltip>
          <Dropdown
            menu={{ items: moveTargets, onClick: ({ key }) => handleMove(key) }}
            trigger={["click"]}
          >
            <Tooltip title={t("Move to")}>
              <Button type="text" icon={<FolderOutlined />} aria-label={t("Move to")} />
            </Tooltip>
          </Dropdown>
          <Popconfirm
            title={t("Delete this message?")}
            onConfirm={handleDelete}
            okText={t("Delete")}
            cancelText={t("Cancel")}
          >
            <Tooltip title={t("Delete")}>
              <Button type="text" danger icon={<DeleteOutlined />} aria-label={t("Delete")} />
            </Tooltip>
          </Popconfirm>
        </Space>
      </div>

      <div className="mail-reader-header">
        <h4 className="mail-reader-subject">{message.subject || t("(no subject)")}</h4>
        <div className="mail-reader-meta">
          <Avatar style={{ backgroundColor: token.colorPrimary, flexShrink: 0 }}>
            {(displayName(sender)[0] || "?").toUpperCase()}
          </Avatar>
          <div className="mail-reader-people">
            <div>
              <strong>{displayName(sender)}</strong>{" "}
              <span className="mail-muted">&lt;{sender.address}&gt;</span>
            </div>
            <div className="mail-muted">
              {t("To")}: {formatAddressList(message.to) || "—"}
            </div>
            {message.cc.length > 0 && (
              <div className="mail-muted">
                {t("Cc")}: {formatAddressList(message.cc)}
              </div>
            )}
            {message.bcc.length > 0 && (
              <div className="mail-muted">
                {t("Bcc")}: {formatAddressList(message.bcc)}
              </div>
            )}
            {message.replyTo.length > 0 &&
              formatAddress(message.replyTo[0]) !== formatAddress(sender) && (
                <div className="mail-muted">
                  {t("Reply to")}: {formatAddressList(message.replyTo)}
                </div>
              )}
          </div>
          <div className="mail-muted mail-reader-date">{formatFullDate(message.date)}</div>
        </div>

        {fileAttachments.length > 0 && (
          <div className="mail-reader-attachments">
            {fileAttachments.map(attachment => (
              <Tag
                key={attachment.index}
                icon={<PaperClipOutlined />}
                className="mail-attachment-chip"
                onClick={() =>
                  mailApi
                    .downloadAttachment(account._id, uid, folder, attachment)
                    .catch(error =>
                      Error(getErrorMessage(error, t("Failed to download attachment")))
                    )
                }
              >
                {attachment.filename}{" "}
                <span className="mail-muted">({formatSize(attachment.size)})</span>
              </Tag>
            ))}
          </div>
        )}

        {showRemoteBanner && (
          <Alert
            type="warning"
            showIcon
            className="mt-2"
            message={t("Remote images are blocked to protect your privacy.")}
            action={
              <Button size="small" onClick={() => setAllowRemote(true)}>
                {t("Show images")}
              </Button>
            }
          />
        )}
      </div>

      {assistant && !isDraftsFolder && (
        <AssistantPanel
          assistant={assistant}
          account={account}
          message={message}
          onCompose={onCompose}
          onMove={handleMove}
        />
      )}

      <MessageBody html={message.html} allowRemote={allowRemote} />
    </div>
  );
};

export default MessageView;
